import React, { useEffect, useState } from 'react';
import { Loading, Text } from '@nextui-org/react';
import { getSessionDetail } from '../../config/sessionsApi';
import { AdminSessionDetail } from '../../types/sessions';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

import ConversationTab from './Tabs/ConversationTab';
import CanvasTab from './Tabs/CanvasTab';
import EventsTab from './Tabs/EventsTab';
import MetricsTab from './Tabs/MetricsTab';

interface SessionDetailProps {
    sessionId: string;
    onClose: () => void;
    onNext?: () => void;
    onPrev?: () => void;
    currentIndex?: number;
    totalCount?: number;
}

const SessionDetail: React.FC<SessionDetailProps> = ({ 
    sessionId, 
    onClose, 
    onNext, 
    onPrev, 
    currentIndex, 
    totalCount 
}) => {
    const [detail, setDetail] = useState<AdminSessionDetail | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [activeTab, setActiveTab] = useState<'conversation' | 'canvas' | 'events' | 'metrics'>('conversation');

    useEffect(() => {
        const fetchDetail = async () => {
            setIsLoading(true);
            try {
                const data = await getSessionDetail(sessionId);
                setDetail(data);
            } catch (error) {
                console.error("Failed to load session details", error);
            } finally {
                setIsLoading(false);
            }
        };
        fetchDetail();
    }, [sessionId]);

    const tabStyle = (tab: string) => ({
        padding: '0.5rem 1rem',
        cursor: 'pointer',
        borderBottom: activeTab === tab ? '2px solid #0070f3' : '2px solid transparent',
        fontWeight: activeTab === tab ? 'bold' : 'normal',
        background: 'none',
        borderTop: 'none',
        borderLeft: 'none',
        borderRight: 'none',
        outline: 'none',
        color: activeTab === tab ? '#0070f3' : '#666'
    });

    return (
        <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            zIndex: 9999,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '2rem'
        }}>
            <div style={{
                background: 'white',
                width: '100%',
                height: '100%',
                borderRadius: '12px',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)'
            }}>
                {/* Header Container */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '1rem 1.5rem',
                    borderBottom: '1px solid #eaeaea',
                    backgroundColor: '#fafafa'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                        <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 600 }}>
                            Session Detail: <span style={{ fontSize: '1rem', fontWeight: 'normal', color: '#666' }}>{sessionId}</span>
                        </h2>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem' }}>
                        {currentIndex !== undefined && totalCount !== undefined && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                                <button 
                                    onClick={onPrev} 
                                    disabled={!onPrev}
                                    style={{
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        width: '32px', height: '32px', borderRadius: '4px',
                                        border: '1px solid #eaeaea', background: onPrev ? 'white' : '#f5f5f5',
                                        cursor: onPrev ? 'pointer' : 'not-allowed', color: onPrev ? '#333' : '#a1a1aa'
                                    }}
                                    title="Previous Session"
                                >
                                    <ChevronLeft size={18} />
                                </button>
                                <span style={{ fontSize: '0.875rem', fontWeight: 500, color: '#666', minWidth: '50px', textAlign: 'center' }}>
                                    {currentIndex} / {totalCount}
                                </span>
                                <button 
                                    onClick={onNext} 
                                    disabled={!onNext}
                                    style={{
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        width: '32px', height: '32px', borderRadius: '4px',
                                        border: '1px solid #eaeaea', background: onNext ? 'white' : '#f5f5f5',
                                        cursor: onNext ? 'pointer' : 'not-allowed', color: onNext ? '#333' : '#a1a1aa'
                                    }}
                                    title="Next Session"
                                >
                                    <ChevronRight size={18} />
                                </button>
                            </div>
                        )}
                        
                        <div style={{ width: '1px', height: '24px', backgroundColor: '#eaeaea' }}></div>

                        <button 
                            onClick={onClose}
                            style={{
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                width: '32px', height: '32px', borderRadius: '4px',
                                border: 'none', background: '#fee2e2', color: '#ef4444',
                                cursor: 'pointer'
                            }}
                            title="Close"
                        >
                            <X size={18} />
                        </button>
                    </div>
                </div>

                {/* Body Container */}
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', padding: '1.5rem' }}>
                    {isLoading ? (
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                            <Loading size="xl" />
                        </div>
                    ) : !detail ? (
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                            <Text>No detail found.</Text>
                        </div>
                    ) : (
                        <>
                            <div style={{ borderBottom: '1px solid #eaeaea', marginBottom: '1rem', display: 'flex', gap: '1rem' }}>
                                <button style={tabStyle('conversation')} onClick={() => setActiveTab('conversation')}>Conversation</button>
                                <button style={tabStyle('canvas')} onClick={() => setActiveTab('canvas')}>Canvas</button>
                                <button style={tabStyle('events')} onClick={() => setActiveTab('events')}>Events</button>
                                <button style={tabStyle('metrics')} onClick={() => setActiveTab('metrics')}>Metrics</button>
                            </div>

                            <div style={{ flex: 1, overflowY: 'auto' }}>
                                {activeTab === 'conversation' && <ConversationTab detail={detail} />}
                                {activeTab === 'canvas' && <CanvasTab detail={detail} />}
                                {activeTab === 'events' && <EventsTab detail={detail} />}
                                {activeTab === 'metrics' && <MetricsTab detail={detail} />}
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SessionDetail;